// @flow

import React from 'react';
import { connect } from 'react-redux';
import AvatarHoverCardFollowingText from 'app/common/react/components/AvatarHoverCard/AvatarHoverCardFollowingText';
import i18n from 'app/common/lib/i18n';
import Toast from 'app/common/react/ui/Toast';
import type { Dispatch } from 'redux';
import type { State as ReduxState } from 'app/common/redux/types';
import { track } from 'app/common/lib/react/analytics';
import { useToastManagerContext } from 'app/common/react/ui/ToastManagerContext';
import { Box, Touchable } from 'gestalt';
import { follow, unfollow } from 'app/common/redux/actions/user/follow';

type OwnProps = {|
  followerCount?: number,
  fullName: string,
  id: string,
  imageUrl: string,
  isHorizontalDesign: boolean,
  profileUrl: string,
  showFollowerCount: ?boolean,
  viewType?: $Values<PCONST.thrift.ViewType>,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
|};

type StateProps = {|
  follow: (id: string) => void,
  unfollow: (id: string) => void,
  userFollowed?: boolean,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
|};

export function AvatarHoverCardFollowButton({
  followerCount,
  isHorizontalDesign,
  showFollowerCount,
  userFollowed,
  follow,
  id,
  viewParameter,
  viewType,
  fullName,
  imageUrl,
  profileUrl,
}: Props) {
  const { showOneToast } = useToastManagerContext();

  const followText = userFollowed
    ? i18n._('Following', 'User follow button')
    : i18n._('Follow', 'User follow button');

  const trackToggle = (event) =>
    track(event, {
      view: viewType,
      viewParameter,
      component: PCONST.thrift.ComponentType.AVATAR_HOVER_CARD,
      element: PCONST.thrift.ElementType.AVATAR_HOVER_CARD_FOLLOW_BUTTON,
    });

  const toggleFollow = () => {
    if (userFollowed) {
      unfollow(id);
      trackToggle(PCONST.thrift.EventType.USER_UNFOLLOW);
    } else {
      follow(id);
      trackToggle(PCONST.thrift.EventType.USER_FOLLOW);
      showOneToast(({ onHide }) => (
        <Toast
          onHide={onHide}
          text={[
            i18n._('Now following', 'text that comes before a board/user name in toast'),
            fullName,
          ]}
          href={profileUrl}
          imageUrl={imageUrl}
        />
      ));
    }
  };

  return (
    <Box
      alignItems="center"
      dangerouslySetInlineStyle={{ __style: { marginTop: isHorizontalDesign ? '-22px' : 0 } }}
      display="flex"
      justifyContent="center"
      position="relative"
    >
      <Touchable onTouch={toggleFollow} shape="pill">
        <Box
          alignItems="center"
          color={userFollowed ? 'lightGray' : 'red'}
          direction="row"
          display="flex"
          justifyContent="center"
          paddingX={isHorizontalDesign ? 5 : 6}
          paddingY={isHorizontalDesign ? 3 : 4}
          shape="pill"
        >
          <AvatarHoverCardFollowingText
            followerCount={followerCount}
            followText={followText}
            isHorizontalDesign={isHorizontalDesign}
            showFollowers={showFollowerCount && followerCount !== 0}
            userFollowed={userFollowed}
          />
        </Box>
      </Touchable>
    </Box>
  );
}

const mapStateToProps = (state: ReduxState, { id }) => {
  const user = (state.users || {})[id];
  return {
    userFollowed: !!user && user.explicitly_followed_by_me,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  follow: (id) => dispatch(follow(id)),
  unfollow: (id) => dispatch(unfollow(id)),
});

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps,
)(AvatarHoverCardFollowButton);
